import * as angular from "angular";
import { CampaignDataProvider } from "../../data-provider/campaign.dp";
import { IntegrationDataProvider } from "../../data-provider/integration.dp";
import { Campaign } from "../../models/campaign.model";
import { CanvassStatus } from "../../models/canvass-status.model";
import { Feature } from "../../models/feature.model";
import { Integration } from "../../models/integration.model";
import { DialogUtils } from "../../helper/dialog.helper";
import { CanvassStatusDataProvider } from "../../data-provider/canvass-status.dp";
import { ScreenUtils } from "../../helper/screen.helper";
import { PermissionCheck } from "../../shared/permission";

let CanvassStatusList = {
    selector: "vcCanvassStatusList",
    templateUrl:
        "/angular/components/canvass-status/canvass-status-list.component.html",
    bindings: {
        mode: "=?",
        defaultStatuses: "=?",
    },
    controller: class CanvassStatusListController {
        private $scope: any;
        private $timeout: any;
        private __app_type: any;
        private $q: any;
        private $window: any;
        private $location: ng.ILocationService;
        private dataProvider: CanvassStatusDataProvider;
        private dialog: DialogUtils;
        private campaignDataProvider: CampaignDataProvider;
        private integrationDataProvider: IntegrationDataProvider;

        public activeCampaign: Campaign;
        public isLeaderView: boolean;
        public integration: Integration;
        public canvassStatuses: CanvassStatus[];
        public timeoutCode: any;
        public delayInMs: number = 1000;
        public savingStatuses: boolean = false;
        public colors: any[];
        public mode: string;
        public defaultStatuses: any;
        public screen: ScreenUtils;
        public permissionProvider: PermissionCheck;

        constructor(
            $scope: any,
            $timeout: any,
            $q: any,
            $window: any,
            $location: ng.ILocationService,
            __app_type: string,
            DialogUtils: DialogUtils,
            CanvassStatusDataProvider: CanvassStatusDataProvider,
            CampaignDataProvider: CampaignDataProvider,
            ScreenUtils: ScreenUtils,
            IntegrationDataProvider: IntegrationDataProvider,
            PermissionCheck: PermissionCheck
        ) {
            this.$scope = $scope;
            this.dataProvider = CanvassStatusDataProvider;
            this.dialog = DialogUtils;
            this.campaignDataProvider = CampaignDataProvider;
            this.$timeout = $timeout;
            this.__app_type = __app_type;
            this.$q = $q;
            this.integrationDataProvider = IntegrationDataProvider;
            this.$window = $window;
            this.$location = $location;
            this.colors = new CanvassStatus().colors;
            this.screen = ScreenUtils;
            this.permissionProvider = PermissionCheck;

            this.activeCampaign = this.campaignDataProvider.getActiveCampaign();
            this.isLeaderView = this.__app_type == "LEADER";
            this.integrationDataProvider
                .getIntegration("callhub")
                .then((integration) => {
                    this.integration = integration;
                });

            this.$scope.$watch(
                () => this.canvassStatuses,
                (newValue, oldValue) => {
                    if (newValue !== oldValue) {
                        this.saveStatuses();
                    }
                },
                true
            );
        }

        $onInit() {
            if (!this.mode) {
                this.mode = "customize";
            }
            if (this.defaultStatuses) {
                this.canvassStatuses = angular.copy(this.defaultStatuses);
                this.dataProvider.canvassStatuses = this.canvassStatuses;
                this.dataProvider.updateAll(this.canvassStatuses);
            } else {
                this.dataProvider
                    .walkCanvassStatuses()
                    .then((statuses: CanvassStatus[]) => {
                        this.canvassStatuses = statuses;
                    });
            }
        }

        saveStatuses(): void {
            this.$timeout.cancel(this.timeoutCode); //does nothing, if timeout already done
            this.timeoutCode = this.$timeout(() => {
                if (!this.savingStatuses) {
                    this.savingStatuses = true;
                    this.dataProvider.setStatuses(this.canvassStatuses);

                    let data = this.canvassStatuses.map((item) => item["id"]);
                    this.dataProvider.updateOrder(data).then(() => {
                        this.savingStatuses = false;
                    });
                }
            }, this.delayInMs);
        }

        updateColor(status: CanvassStatus, color: string): void {
            status.color = color;
            this.dataProvider.update(status);
        }

        openCanvassStatusDialog(
            canvassStatus: CanvassStatus,
            ev: object
        ): void {
            if (!canvassStatus) {
                canvassStatus = new CanvassStatus();
            }
            this.dialog.show({
                component: "vc-canvass-status-form",
                targetEvent: ev,
                scope: this.$scope,
                clickOutsideToClose: false,
                locals: {
                    selectedStatus: canvassStatus,
                    mode: this.mode,
                },
                ariaLabel: "Create Interaction Status",
            });
        }

        openMigrateCanvassStatusDialog(
            canvassStatus: CanvassStatus,
            ev: object
        ): void {
            if (this.canvassStatuses.length <= 2) {
                this.openMinStatusesDialog(ev);
                return;
            }
            if (this.isMapped(canvassStatus.id)) {
                this.openMappedStatusDialog(ev, "migrate");
                return;
            }
            this.checkConversionRateUsage(canvassStatus.id).then(
                (usedInConversion: boolean): void => {
                    if (usedInConversion) {
                        this.openConversionRateStatusDialog(ev, "migrate");
                        return;
                    }
                    this.dialog.show({
                        component: "vc-migrate-canvass-status",
                        targetEvent: ev,
                        parent: angular.element(document.body),
                        clickOutsideToClose: false,
                        scope: this.$scope,
                        locals: {
                            selectedStatus: canvassStatus,
                        },
                        ariaLabel: "Migrate Interaction Status",
                    });
                }
            );
        }
        openWorkflowCanvassStatusDialog(
            canvassStatus: CanvassStatus,
            ev: object
        ): void {
            if (!canvassStatus) {
                canvassStatus = new CanvassStatus();
            }
            this.dialog.show({
                component: "vc-canvass-status-workflow",
                targetEvent: ev,
                scope: this.$scope,
                clickOutsideToClose: false,
                size: "large",
                locals: {
                    selectedStatus: canvassStatus,
                },
                ariaLabel: "Create Interaction Status Workflow",
            });
        }

        openMinStatusesDialog(ev: object): void {
            const alertDialog = this.dialog
                .alert()
                .textContent(
                    "Sorry but you need to keep at least 2 interaction statuses!"
                )
                .targetEvent(ev)
                .ok("OK");
            this.dialog.showPreConfiguredDialog(alertDialog);
        }

        openDeleteCanvassStatusDialog(
            canvassStatus: CanvassStatus,
            ev: object
        ): void {
            if (this.canvassStatuses.length <= 2) {
                this.openMinStatusesDialog(ev);
                return;
            }
            if (this.isMapped(canvassStatus.id)) {
                this.openMappedStatusDialog(ev, "delete");
                return;
            }
            this.checkConversionRateUsage(canvassStatus.id).then(
                (usedInConversion: boolean): void => {
                    if (usedInConversion) {
                        this.openConversionRateStatusDialog(ev, "delete");
                        return;
                    }
                    this.dialog.show({
                        component: "vc-delete-canvass-status",
                        parent: angular.element(document.body),
                        targetEvent: ev,
                        clickOutsideToClose: false,
                        scope: this.$scope,
                        locals: {
                            selectedStatus: canvassStatus,
                        },
                        ariaLabel: "Delete Interaction Status",
                    });
                }
            );
        }

        onDrop(item: object): CanvassStatus {
            return new CanvassStatus(item);
        }

        isMapped(id: number | string): boolean {
            if (
                this.integration.installed &&
                this.integration.settings &&
                this.integration.settings.statusMapping
            ) {
                for (let key in this.integration.settings.statusMapping) {
                    if (
                        this.integration.settings.statusMapping.hasOwnProperty(
                            key
                        )
                    ) {
                        if (
                            this.integration.settings.statusMapping[key] === id
                        ) {
                            return true;
                        }
                    }
                }
            }
            return false;
        }

        /**
         * Resolves true if the given status id is referenced by the
         * conversion-rate config (in the numerator, or in the custom
         * denominator when one is set). Deleting or migrating such a status
         * would break the conversion formula, so the user is forced to
         * update the conversion settings first.
         */
        checkConversionRateUsage(statusId: number | string): Promise<boolean> {
            return this.permissionProvider
                .getFeature("CONVERSION_RATE_CONFIG")
                .then((feature: Feature): boolean => {
                    const extra: any = feature?.attributes?.extra;
                    if (!extra) return false;
                    const id: number = parseInt(String(statusId), 10);
                    const toIntArr = (raw: unknown): number[] =>
                        Array.isArray(raw)
                            ? raw
                                  .map((n: unknown): number =>
                                      parseInt(String(n), 10)
                                  )
                                  .filter((n: number): boolean => !isNaN(n))
                            : [];
                    const numerator: number[] = toIntArr(extra.numerator);
                    if (numerator.indexOf(id) !== -1) return true;
                    // A null denominator means "all interactions", so a
                    // specific status isn't pinned in the formula.
                    if (extra.denominator === null || extra.denominator === undefined) {
                        return false;
                    }
                    const denominator: number[] = toIntArr(extra.denominator);
                    return denominator.indexOf(id) !== -1;
                });
        }

        openConversionRateStatusDialog(ev: object, actionType: string): void {
            const customizePath: string = this.isLeaderView
                ? "/leader_customize"
                : "/customize";
            const title: string =
                actionType.charAt(0).toUpperCase() +
                actionType.substring(1) +
                " Status";
            const importDialog = this.dialog
                .confirm()
                .title(title)
                .htmlContent(
                    "This interaction status is currently used to calculate the conversion rate.<br>" +
                        "Please update the conversion rate settings to remove it before you can " +
                        actionType +
                        " this status."
                )
                .targetEvent(ev)
                .cancel("Cancel")
                .ok("Conversion rate settings");
            this.dialog.show(importDialog).then((): void => {
                this.$location.path(customizePath).search({ tab: "conversion" });
            });
        }

        openMappedStatusDialog(ev: object, actionType: string): void {
            let importDialog = this.dialog
                .confirm()
                .title(
                    actionType.charAt(0).toUpperCase() +
                        actionType.substring(1) +
                        " Status"
                )
                .htmlContent(
                    "This interaction status is currently mapped to a " +
                        this.integration.title +
                        " disposition.<br>" +
                        "Please go to your " +
                        this.integration.title +
                        " integration settings to remove that mapping " +
                        "before you can " +
                        actionType +
                        " this status."
                )
                .targetEvent(ev)
                .cancel("Not Now")
                .ok(this.integration.title + " integration settings ");
            this.dialog.show(importDialog).then(() => {
                this.$window.location.href =
                    "./#/integration/" + this.integration.key;
            });
        }
    },
};

angular
    .module("canvassStatus")
    .component(CanvassStatusList.selector, CanvassStatusList);
